var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useMemo, useRef, useState } from "react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import { toast } from "sonner";
import { LogType } from "@onvo-ai/js";
import { ArrowsRightLeftIcon, DocumentChartBarIcon, DocumentDuplicateIcon, PencilSquareIcon, PhotoIcon, TrashIcon, } from "@heroicons/react/24/outline";
import ChartBase from "./ChartBase";
import { Card } from "../../tremor/Card";
import { Icon } from "../../tremor/Icon";
import { useDashboard } from "../../layouts/Dashboard/useDashboard";
import { useBackend } from "../../layouts/Wrapper";
import Skeletons from "../Skeletons";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuIconWrapper, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSubMenu, DropdownMenuSubMenuContent, DropdownMenuSubMenuTrigger, DropdownMenuTrigger, } from "../../tremor/DropdownMenu";
import { useTextWidgetModal } from "../TextWidgetModal";
import { useImageWidgetModal } from "../ImageWidgetModal";
import { useEditWidgetModal } from "../EditWidgetModal";
import { useTheme } from "../../layouts/Dashboard/useTheme";
import { ExclamationTriangleIcon } from "@heroicons/react/16/solid";
import { useManualWidgetModal } from "../ManualWidgetModal";
const DragHandle = () => {
    return (React.createElement("div", { className: "onvo-absolute -onvo-top-6 onvo-w-10 onvo-h-6 onvo-z-10 onvo-hidden group-hover/chartcard:onvo-flex onvo-transition-all onvo-left-[50%] -onvo-ml-5 onvo-justify-center onvo-items-center onvo-foreground-color onvo-border-solid onvo-border onvo-border-b-0 onvo-border-black/5 dark:onvo-border-white/10 onvo-rounded-t-md" },
        React.createElement("div", { className: "onvo-chart-card-drag-handle  onvo-absolute onvo-grid-cols-4 onvo-py-1 onvo-cursor-move onvo-grid onvo-px-1 onvo-items-center onvo-justify-center onvo-h-6 onvo-w-10" },
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }),
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }),
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }),
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }),
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }),
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }),
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }),
            React.createElement("div", { className: "onvo-h-[3px] onvo-w-[3px] onvo-ml-0.5 onvo-rounded-full onvo-bg-black/20 dark:onvo-bg-white/20" }))));
};
const ChartCard = ({ widget, className, footer, hideOptions }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const { dashboard, refreshWidgets, tab } = useDashboard();
    const { setOpen: setEditModalOpen } = useEditWidgetModal();
    const { setOpen: setTextModalOpen } = useTextWidgetModal();
    const { setOpen: setImageModalOpen } = useImageWidgetModal();
    const { setOpen: setManualModalOpen } = useManualWidgetModal();
    const { backend, adminMode, containerRef } = useBackend();
    const theme = useTheme();
    const cardRef = useRef(null);
    const [cache, setCache] = useState(null);
    const duplicate = () => __awaiter(void 0, void 0, void 0, function* () {
        let newObj = Object.assign({}, widget);
        delete newObj.id;
        if (!backend)
            return;
        toast.promise(() => __awaiter(void 0, void 0, void 0, function* () {
            let wid = yield backend.widgets.create(newObj);
            yield backend.widget(wid.id).executeCode();
            return wid;
        }), {
            loading: "Duplicating widget...",
            success: (newWidget) => {
                refreshWidgets(backend);
                backend.logs.create({
                    type: LogType.CreateWidget,
                    dashboard: widget.dashboard,
                    widget: newWidget.id,
                });
                return "Widget duplicated";
            },
            error: (error) => "Error duplicating widget: " + error.message,
        });
    });
    const moveTab = (newTab) => __awaiter(void 0, void 0, void 0, function* () {
        let newObj = Object.assign(Object.assign({}, widget), { tab: newTab });
        if (!backend)
            return;
        toast.promise(() => {
            return backend.widgets.update(widget.id, newObj);
        }, {
            loading: "Moving widget...",
            success: (newWidget) => {
                refreshWidgets(backend);
                return "Widget moved";
            },
            error: (error) => "Error moving widget: " + error.message,
        });
    });
    const deleteWidget = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!backend)
            return;
        toast.promise(() => {
            return backend.widgets.delete(widget.id);
        }, {
            loading: "Deleting widget...",
            success: () => {
                backend.logs.create({
                    type: LogType.DeleteWidget,
                    dashboard: widget.dashboard
                });
                refreshWidgets(backend);
                return "Widget deleted";
            },
            error: (error) => "Error deleting widget: " + error.message,
        });
    });
    const exportWidget = (format) => {
        if (!backend)
            return;
        toast.promise(() => {
            return backend.widget(widget.id).export(format, theme);
        }, {
            loading: `Exporting widget as ${format}...`,
            success: (blob) => {
                let blobUrl = window.URL.createObjectURL(blob);
                let a = document.createElement("a");
                a.download = widget.title + "." + format;
                a.href = blobUrl;
                document.body.appendChild(a);
                a.click();
                a.remove();
                return "Widget exported";
            },
            error: (error) => "Error exporting widget: " + error.message,
        });
    };
    useEffect(() => {
        if (!widget)
            return;
        if (!backend)
            return;
        backend.widget(widget.id).cache().then((cacheData) => {
            setCache(cacheData);
        });
    }, [widget, backend]);
    const layout_editable = adminMode || ((_a = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _a === void 0 ? void 0 : _a.can_edit_widget_layout);
    const widget_editable = adminMode || ((_b = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _b === void 0 ? void 0 : _b.can_edit_widgets);
    const addable = adminMode || ((_c = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _c === void 0 ? void 0 : _c.can_create_widgets);
    const deletable = adminMode || ((_d = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _d === void 0 ? void 0 : _d.can_delete_widgets);
    const ImageDownloadEnabled = useMemo(() => {
        const isTable = widget.type === "table";
        if (isTable)
            return false;
        if (adminMode)
            return true;
        if ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) && dashboard.settings.disable_download_images)
            return false;
        return true;
    }, [dashboard, widget, adminMode]);
    const ReportDownloadEnabled = useMemo(() => {
        if (adminMode)
            return true;
        if ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) && dashboard.settings.disable_download_reports)
            return false;
        return true;
    }, [dashboard, widget, adminMode]);
    const error = useMemo(() => {
        if (widget && widget.error && widget_editable) {
            return (React.createElement("div", { onClick: () => setEditModalOpen(true, widget), className: "onvo-absolute onvo-right-4 onvo-bottom-4 onvo-rounded-full onvo-shadow-lg onvo-foreground-color onvo-z-10 onvo-border onvo-border-black/5 dark:onvo-border-white/10" },
                React.createElement("div", { className: "onvo-flex onvo-cursor-pointer onvo-items-center onvo-justify-center onvo-size-10" },
                    React.createElement(ExclamationTriangleIcon, { className: "onvo-text-red-500 onvo-size-5" }))));
        }
        return React.createElement(React.Fragment, null);
    }, [widget]);
    // @ts-ignore
    const Skeleton = Skeletons[widget.type];
    const moveDropdown = useMemo(() => {
        if (((dashboard === null || dashboard === void 0 ? void 0 : dashboard.tabs) || []).filter(a => !a.hidden).length <= 1) {
            return React.createElement(React.Fragment, null);
        }
        return (React.createElement(DropdownMenuSubMenu, null,
            React.createElement(DropdownMenuSubMenuTrigger, null,
                React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                    React.createElement(ArrowsRightLeftIcon, { className: "onvo-size-4" }),
                    React.createElement("span", null, "Move widget"))),
            React.createElement(DropdownMenuSubMenuContent, null, dashboard === null || dashboard === void 0 ? void 0 : dashboard.tabs.map(a => (React.createElement(DropdownMenuItem, { key: a.id, onClick: () => moveTab(a.id) },
                React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                    React.createElement("span", null, a.label))))))));
    }, [tab, dashboard]);
    if (widget.type === "divider") {
        return (React.createElement("div", { ref: cardRef, className: "onvo-group/chartcard onvo-relative onvo-h-full onvo-w-full onvo-py-0 !onvo-bg-transparent !onvo-border-0 !onvo-ring-0 !onvo-shadow-none onvo-px-0 " +
                (className || "") },
            layout_editable && !hideOptions && React.createElement(DragHandle, null),
            deletable && !hideOptions && (React.createElement(DropdownMenu, null,
                React.createElement(DropdownMenuTrigger, { asChild: true },
                    React.createElement(Icon, { className: "!onvo-rounded-full onvo-z-20 group-hover/chartcard:onvo-opacity-100 onvo-transition-all onvo-opacity-0  onvo-cursor-pointer onvo-font-override onvo-absolute onvo-top-1 onvo-right-1", icon: EllipsisVerticalIcon, variant: "shadow" })),
                React.createElement(DropdownMenuContent, { container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, className: "onvo-min-w-56 onvo-z-50" }, deletable && (React.createElement(DropdownMenuGroup, null,
                    React.createElement(DropdownMenuItem, { onClick: deleteWidget },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(TrashIcon, { className: "onvo-size-4 onvo-text-red-500" }),
                            React.createElement("span", { className: "onvo-text-red-500" }, "Delete widget")))))))),
            React.createElement(ChartBase, { json: cache, title: "" }),
            footer));
    }
    if (widget.type === "text") {
        let config = widget.config;
        let sub = (config === null || config === void 0 ? void 0 : config.options.plugins.subtitle.text) || "";
        if (typeof sub !== "string") {
            sub = sub.join("<br />");
        }
        return (React.createElement("div", { ref: cardRef, className: "onvo-group/chartcard onvo-relative onvo-h-full onvo-w-full onvo-py-0 !onvo-bg-transparent !onvo-border-0 !onvo-ring-0 !onvo-shadow-none onvo-px-0 " +
                (className || "") },
            layout_editable && !hideOptions && React.createElement(DragHandle, null),
            (widget_editable || deletable) && !hideOptions && (React.createElement(DropdownMenu, null,
                React.createElement(DropdownMenuTrigger, { asChild: true },
                    React.createElement(Icon, { className: "!onvo-rounded-full onvo-z-20 group-hover/chartcard:onvo-opacity-100 onvo-transition-all onvo-opacity-0 onvo-cursor-pointer onvo-font-override onvo-absolute onvo-top-1 onvo-right-1", icon: EllipsisVerticalIcon, variant: "shadow" })),
                React.createElement(DropdownMenuContent, { container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, className: "onvo-min-w-56 onvo-z-50" },
                    widget_editable && (React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: () => {
                                setTimeout(() => {
                                    var _a, _b, _c, _d;
                                    setTextModalOpen(true, {
                                        id: widget.id,
                                        title: widget.title,
                                        subtitle: sub,
                                        prompt: config === null || config === void 0 ? void 0 : config.options.plugins.text.prompt,
                                        titleAlignment: ((_b = (_a = config === null || config === void 0 ? void 0 : config.options.plugins) === null || _a === void 0 ? void 0 : _a.title) === null || _b === void 0 ? void 0 : _b.align) || "start",
                                        descriptionAlignment: ((_d = (_c = config === null || config === void 0 ? void 0 : config.options.plugins) === null || _c === void 0 ? void 0 : _c.subtitle) === null || _d === void 0 ? void 0 : _d.align) || "start",
                                    });
                                }, 30);
                            } },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(PencilSquareIcon, { className: "onvo-size-4" }),
                                React.createElement("span", null, "Edit widget"))),
                        moveDropdown)),
                    widget_editable && deletable && React.createElement(DropdownMenuSeparator, null),
                    deletable && (React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: deleteWidget },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(TrashIcon, { className: "onvo-size-4 onvo-text-red-500" }),
                                React.createElement("span", { className: "onvo-text-red-500" }, "Delete widget")))))))),
            React.createElement(ChartBase, { json: cache, title: widget.title }),
            footer));
    }
    if (widget.type === "image") {
        return (React.createElement("div", { ref: cardRef, className: "onvo-chart-card onvo-group/chartcard onvo-rounded-2xl onvo-transition-all onvo-relative onvo-h-full onvo-w-full onvo-py-0 !onvo-bg-transparent !onvo-border-0 !onvo-ring-0 !onvo-shadow-none onvo-px-0 " +
                (className || "") },
            layout_editable && !hideOptions && React.createElement(DragHandle, null),
            (widget_editable || deletable) && !hideOptions && (React.createElement(DropdownMenu, null,
                React.createElement(DropdownMenuTrigger, { asChild: true },
                    React.createElement(Icon, { className: "!onvo-rounded-full onvo-z-20 group-hover/chartcard:onvo-opacity-100 onvo-opacity-0 onvo-cursor-pointer onvo-font-override onvo-absolute onvo-top-1 onvo-right-1", icon: EllipsisVerticalIcon, variant: "shadow" })),
                React.createElement(DropdownMenuContent, { container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, className: "onvo-min-w-56 onvo-z-50" },
                    widget_editable && (React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: () => {
                                setTimeout(() => {
                                    var _a, _b, _c, _d, _e, _f;
                                    setImageModalOpen(true, {
                                        id: widget.id,
                                        url: ((_c = (_b = (_a = widget.config) === null || _a === void 0 ? void 0 : _a.options.plugins) === null || _b === void 0 ? void 0 : _b.image) === null || _c === void 0 ? void 0 : _c.url) || "",
                                        imageFill: ((_f = (_e = (_d = widget.config) === null || _d === void 0 ? void 0 : _d.options.plugins) === null || _e === void 0 ? void 0 : _e.image) === null || _f === void 0 ? void 0 : _f.fill) || "fill",
                                    });
                                }, 30);
                            } },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(PencilSquareIcon, { className: "onvo-size-4" }),
                                React.createElement("span", null, "Edit widget"))),
                        moveDropdown)),
                    widget_editable && deletable && React.createElement(DropdownMenuSeparator, null),
                    deletable && (React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: deleteWidget },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(TrashIcon, { className: "onvo-size-4 onvo-text-red-500" }),
                                React.createElement("span", { className: "onvo-text-red-500" }, "Delete widget")))))))),
            React.createElement(ChartBase, { json: cache, title: "" }),
            footer));
    }
    return (React.createElement(Card, { ref: cardRef, key: widget.id, id: (_e = widget.settings) === null || _e === void 0 ? void 0 : _e.css_id, className: "onvo-chart-card onvo-h-full onvo-group/chartcard onvo-foreground-color onvo-relative onvo-flex onvo-flex-col onvo-px-0 onvo-py-0 " +
            (className || "") +
            " " +
            (((_f = widget.settings) === null || _f === void 0 ? void 0 : _f.css_classnames) ? (_g = widget.settings) === null || _g === void 0 ? void 0 : _g.css_classnames : "") },
        error,
        layout_editable && !hideOptions && React.createElement(DragHandle, null),
        (addable ||
            deletable ||
            widget_editable ||
            ImageDownloadEnabled ||
            ReportDownloadEnabled) &&
            !hideOptions && (React.createElement(DropdownMenu, { modal: false },
            React.createElement(DropdownMenuTrigger, { asChild: true },
                React.createElement(Icon, { onClick: (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }, onMouseDown: (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }, onMouseUp: (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }, className: "!onvo-rounded-full onvo-z-20 onvo-transition-all group-hover/chartcard:onvo-opacity-100 onvo-opacity-0 onvo-cursor-pointer !onvo-text-gray-500 onvo-absolute onvo-top-1 onvo-right-1", icon: EllipsisVerticalIcon, variant: "shadow" })),
            React.createElement(DropdownMenuContent, { container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, className: "onvo-min-w-56 onvo-background-color onvo-border-black/10 dark:onvo-border-white/10 onvo-z-50" },
                (addable || widget_editable) && (React.createElement(React.Fragment, null,
                    React.createElement(DropdownMenuLabel, null, "Edit widget"),
                    React.createElement(DropdownMenuGroup, null,
                        widget_editable && (React.createElement(React.Fragment, null,
                            React.createElement(DropdownMenuItem, { onClick: () => {
                                    if (widget.engine === "python-v1") {
                                        setEditModalOpen(true, widget);
                                    }
                                    else {
                                        setManualModalOpen(true, widget);
                                    }
                                } },
                                React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                    React.createElement(PencilSquareIcon, { className: "onvo-size-4" }),
                                    React.createElement("span", null, "Edit widget"))),
                            moveDropdown)),
                        addable && (React.createElement(DropdownMenuItem, { onClick: duplicate },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(DropdownMenuIconWrapper, null,
                                    React.createElement(DocumentDuplicateIcon, { className: "onvo-size-4" })),
                                React.createElement("span", null, "Duplicate widget"))))))),
                (addable || widget_editable) && ReportDownloadEnabled && (React.createElement(DropdownMenuSeparator, { className: "onvo-border-black/10 dark:onvo-border-white/10" })),
                ReportDownloadEnabled && (React.createElement(React.Fragment, null,
                    React.createElement(DropdownMenuLabel, null, "Download reports"),
                    React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: () => exportWidget("xlsx") },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(DocumentChartBarIcon, { className: "onvo-size-4" }),
                                React.createElement("span", null, "Download as excel"))),
                        React.createElement(DropdownMenuItem, { onClick: () => exportWidget("csv") },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(DropdownMenuIconWrapper, null,
                                    React.createElement(DocumentChartBarIcon, { className: "onvo-size-4" })),
                                React.createElement("span", null, "Download as CSV")))))),
                (addable || widget_editable || ReportDownloadEnabled) &&
                    ImageDownloadEnabled && React.createElement(DropdownMenuSeparator, { className: "onvo-border-black/10 dark:onvo-border-white/10" }),
                ImageDownloadEnabled && (React.createElement(React.Fragment, null,
                    React.createElement(DropdownMenuLabel, null, "Download images"),
                    React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: () => exportWidget("svg") },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(PhotoIcon, { className: "onvo-size-4" }),
                                React.createElement("span", null, "Download as SVG"))),
                        React.createElement(DropdownMenuItem, { onClick: () => exportWidget("png") },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(DropdownMenuIconWrapper, null,
                                    React.createElement(PhotoIcon, { className: "onvo-size-4" })),
                                React.createElement("span", null, "Download as PNG"))),
                        React.createElement(DropdownMenuItem, { onClick: () => exportWidget("jpeg") },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(DropdownMenuIconWrapper, null,
                                    React.createElement(PhotoIcon, { className: "onvo-size-4" })),
                                React.createElement("span", null, "Download as JPEG")))))),
                (addable ||
                    widget_editable ||
                    ImageDownloadEnabled ||
                    ReportDownloadEnabled) &&
                    deletable && React.createElement(DropdownMenuSeparator, { className: "onvo-border-black/10 dark:onvo-border-white/10" }),
                deletable && (React.createElement(DropdownMenuGroup, null,
                    React.createElement(DropdownMenuItem, { onClick: deleteWidget },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(TrashIcon, { className: "onvo-size-4 onvo-text-red-500" }),
                            React.createElement("span", { className: "onvo-text-red-500" }, "Delete widget")))))))),
        !cache && (React.createElement(Skeleton, null)),
        cache && (React.createElement("div", { className: "onvo-py-3 onvo-px-4 onvo-h-full onvo-w-full onvo-flex onvo-flex-grow onvo-flex-col" },
            React.createElement(ChartBase, { json: cache, title: widget.title, settings: widget.settings }))),
        footer));
};
export default ChartCard;
//# sourceMappingURL=ChartCard.js.map